<template>
  <v-container fluid>
    <SearchBar v-model="search" />

    <v-card outlined class="rounded-xl mt-5">
      <ListTitle
        title="Product"
        @new="$router.push({ path: '/inventory/product/create' })"
      />
      <v-card-text>
        <v-data-table
          class="table-header"
          :headers="headers"
          :items="products"
          :loading="loading"
          :options.sync="options"
          :server-items-length="count"
          :footer-props="footerProps"
          :expand="expand"
          item-key="id"
        >
          <!-- Row -->
          <template slot="item" slot-scope="props">
            <tr class="pointer">
              <td
                class="text-xs-left large-td"
                @click="props.expanded = !props.expanded"
              >
                <v-layout row>
                  <span>{{ props.item.name }}</span>
                  <span class="ml-2" v-if="props.item.imageURL">
                    <v-img
                      width="40px"
                      :src="props.item.imageURL"
                      aspect-ratio="1"
                      max-height="20px"
                      contain
                    />
                  </span>
                </v-layout>
              </td>

              <td
                class="text-xs-left medium-td"
                @click="props.expanded = !props.expanded"
              >
                {{ props.item.Category.name }}
              </td>

              <td
                class="text-xs-left"
                @click="props.expanded = !props.expanded"
              >
                {{ props.item.GlobalStock ? props.item.GlobalStock : "N/A" }}
              </td>

              <td class="text-right" @click="props.expanded = !props.expanded">
                {{ props.item.sellingPrice | currency }}
              </td>

              <td @click="props.expanded = !props.expanded">
                <toggle-button
                  disabled
                  :value="props.item.active"
                  :labels="true"
                  :sync="true"
                />
              </td>

              <td class="justify-end layout px-0">
                <v-menu bottom left offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on">
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>

                  <v-list class="menu-button">
                    <v-list-item @click="editProduct(props.item)">
                      <v-list-item-title>Edit</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="deleteProduct(props.item)">
                      <v-list-item-title>Delete</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>

          <!-- Expanded -->
          <template slot="expanded-item" slot-scope="props">
            <!-- IMPORTANT: colspan must match header count -->
            <td :colspan="headers.length">
              <v-card flat>
                <v-card-text>
                  <v-layout align-start justify-start row fill-height>
                    <v-img
                      width="120px"
                      v-if="props.item.imageURL"
                      :src="props.item.imageURL"
                      aspect-ratio="1"
                      max-height="120px"
                      max-width="120px"
                      contain
                    />
                    <v-flex class="ml-2">
                      <div>
                        {{ $t("labels.upc") }}:
                        <span class="font-weight-medium">
                          {{ props.item.upc ? props.item.upc : "N/A" }}
                        </span>
                      </div>
                      <div>
                        {{ $t("labels.cost") }}:
                        <span class="font-weight-medium">
                          {{ props.item.purchaseCost | currency }}
                        </span>
                      </div>
                      <div>
                        {{ $t("labels.inventoryValuation") }}:
                        <span class="font-weight-medium">
                          {{
                            $options.filters.currency(
                              parseFloat(props.item.purchaseCost) *
                                parseFloat(props.item.GlobalStock)
                            )
                          }}
                        </span>
                      </div>
                      <div>
                        {{ $t("labels.totalSellingValue") }}:
                        <span class="font-weight-medium">
                          {{
                            $options.filters.currency(
                              parseFloat(props.item.sellingPrice) *
                                parseFloat(props.item.GlobalStock)
                            )
                          }}
                        </span>
                      </div>
                      <div>
                        {{ $t("labels.description") }}:
                        <span class="font-weight-medium">
                          {{
                            props.item.description
                              ? props.item.description
                              : "N/A"
                          }}
                        </span>
                      </div>
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import productService from "../service";

export default {
  name: "product-list",
  data() {
    return {
      expand: false,
      headers: [
        { text: this.$t("labels.name"), value: "name", sortable: true },
        {
          text: this.$t("labels.category"),
          value: "category",
          sortable: false,
        },
        { text: this.$t("labels.stock"), value: "balance", sortable: false },
        {
          text: this.$t("labels.price"),
          value: "price",
          align: "right",
          sortable: false,
        },
        { text: this.$t("labels.active"), value: "active", sortable: false },
        { text: "", value: "action", sortable: false },
      ],
      search: null,
      products: [],
      count: 0,
      loading: false,

      // ✅ Vuetify 2 replacement for pagination
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["name"],
        sortDesc: [false],
      },

      footerProps: {
        itemsPerPageOptions: [10, 25, 50, 100],
      },
    };
  },

  watch: {
    search(val) {
      const v = val || "";
      if (v.length > 3 || v.length === 0) this.getList();
    },

    options: {
      handler() {
        this.getList();
      },
      deep: true,
    },
  },

  mounted() {
    this.getList();
  },

  methods: {
    getList() {
      this.loading = true;

      const page = this.options.page || 1;
      const itemsPerPage = this.options.itemsPerPage || 10;

      const sortBy = (this.options.sortBy && this.options.sortBy[0]) || "name";
      const sortDesc =
        (this.options.sortDesc && this.options.sortDesc[0]) || false;

      const offset = itemsPerPage * page - itemsPerPage;

      return productService
        .getAll({
          limit: itemsPerPage,
          offset,
          showBalance: true,
          search: this.search,
          orderBy: [sortBy, sortDesc],
        })
        .then((response) => {
          this.products = response.data;
          this.count = response.count;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    editProduct(product) {
      this.$router.push({ name: "product-edit", params: product });
    },

    deleteProduct(product) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.value) {
          productService.delete(product.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Product has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>